import { useMemo, useState } from 'react';
import { Box, Typography, Chip, TextField, MenuItem, Tooltip, IconButton, Alert } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPremium, GridColDef, GridRenderCellParams, GridPaginationModel, GridSortModel,
} from '@mui/x-data-grid-premium';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { trackingService } from '../../services/tracking.service';
import DateRangePicker from '../../components/DateRangePicker';
import TimezoneSelect from '../../components/TimezoneSelect';
import { useGridHeight } from '../../hooks/useGridHeight';
import { dayRangeParams, formatInOffset } from '../../utils/timezone';

// unknown_click can mean real lost attribution (hence the warning); the rest are sender-side.
const REASON_META: Record<string, { label: string; color: 'warning' | 'default' }> = {
  unknown_click: { label: 'Unknown click', color: 'warning' },
  unsubstituted_macro: { label: 'Unsubstituted macro', color: 'default' },
  foreign_id_format: { label: 'Foreign id format', color: 'default' },
  missing_cid: { label: 'Missing click id', color: 'default' },
};
const reasonLabel = (r: string): string => REASON_META[r]?.label ?? r;

const DEFAULT_FROM = new Date(Date.now() - 7 * 86400_000);

export default function UnmatchedPostbacksTab() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([DEFAULT_FROM, new Date()]);
  const [timezone, setTimezone] = useState(0);
  const [reason, setReason] = useState('');
  const [senderIp, setSenderIp] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const [{ page, pageSize }, setPagination] = useState({ page: 0, pageSize: 100 });
  const gridHeight = useGridHeight();

  const sort = sortModel[0];
  const params = {
    ...dayRangeParams(dateRange),
    timezone,
    reason: reason || undefined,
    sender_ip: senderIp || undefined,
    order_by: sort?.field,
    order: (sort?.sort ?? 'desc') as 'asc' | 'desc',
    limit: pageSize,
    offset: page * pageSize,
  };

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['unmatched-postbacks', 'list', params],
    queryFn: () => trackingService.getUnmatchedPostbacks(params),
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => (data?.data ?? []).map((r, i) => ({ id: `${r.created_at}-${r.sender_ip}-${i}`, ...r })),
    [data],
  );

  const columns: GridColDef[] = [
    { field: 'created_at', headerName: 'Time', width: 190, valueFormatter: (v: string) => formatInOffset(v, timezone) },
    {
      field: 'reason', headerName: 'Reason', width: 190,
      renderCell: (p: GridRenderCellParams) => (
        <Chip size="small" variant="outlined" label={reasonLabel(p.value)} color={REASON_META[p.value]?.color ?? 'default'} />
      ),
    },
    { field: 'sender_ip', headerName: 'Sender IP', width: 150, renderCell: (p) => p.value || '(no ip)' },
    {
      field: 'network_name', headerName: 'Network', width: 150, sortable: false,
      valueGetter: (_v, row) => row.network_name || (row.network_seq ? `nid ${row.network_seq}` : '-'),
    },
    { field: 'event_type', headerName: 'Event', width: 110, valueFormatter: (v: string) => v || '-' },
    { field: 'payout', headerName: 'Payout', width: 90, type: 'number' },
    { field: 'currency', headerName: 'Cur', width: 70, valueFormatter: (v: string) => v || '-' },
    { field: 'click_id', headerName: 'Click ID', width: 200, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }} noWrap>{p.value || '-'}</Typography>
      ),
    },
    { field: 'referer', headerName: 'Referer', width: 200, sortable: false, valueFormatter: (v: string) => v || '-' },
    {
      field: 'request_url', headerName: 'Request URL', flex: 1, minWidth: 200, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip title={p.value || ''}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} noWrap>{p.value || '-'}</Typography>
        </Tooltip>
      ),
    },
    {
      field: 'request_body', headerName: 'Request Body', width: 180, sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip title={p.value || ''}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} noWrap>{p.value || '-'}</Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Reason" sx={{ width: 190 }}
          value={reason} onChange={(e) => { setReason(e.target.value); setPagination((s) => ({ ...s, page: 0 })); }}
        >
          <MenuItem value="">All reasons</MenuItem>
          {Object.entries(REASON_META).map(([value, m]) => (
            <MenuItem key={value} value={value}>{m.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Sender IP" sx={{ width: 160 }} placeholder="contains…"
          value={senderIp} onChange={(e) => { setSenderIp(e.target.value); setPagination((s) => ({ ...s, page: 0 })); }}
        />
        <Box sx={{ flex: 1 }} />
        <TimezoneSelect value={timezone} onChange={setTimezone} />
        <DateRangePicker value={dateRange} onChange={(v) => { setDateRange(v); setPagination((s) => ({ ...s, page: 0 })); }} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => refetch()}><Refresh fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load unmatched postbacks.</Alert>}

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        density="compact"
        rowCount={data?.total ?? 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m: GridPaginationModel) => setPagination({
          page: m.pageSize !== pageSize ? 0 : m.page,
          pageSize: m.pageSize,
        })}
        pageSizeOptions={[50, 100, 200, 400]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(m: GridSortModel) => { setSortModel(m); setPagination((s) => ({ ...s, page: 0 })); }}
        disableRowSelectionOnClick
        // Off because the grid's column filter only sees the loaded page; filtering is server-side (above).
        disableColumnFilter
        localeText={{ noRowsLabel: 'No unmatched postbacks in this range.' }}
        sx={{
          bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, height: gridHeight,
          '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: 'action.hover' },
        }}
      />
    </Box>
  );
}
